import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Users, UserPlus, Search, ChevronLeft, ChevronRight, Loader2, AlertCircle, RefreshCw, Link2, X, Plus, MoreVertical, Trash2, CalendarClock, PlusCircle, Crown, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { computeTier, DEFAULT_TIERS } from '../../utils/loyaltyTiers';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs } from 'firebase/firestore';
import PageHeader from '../../components/common/PageHeader';
import { useAuth } from '../../context/AuthContext';
import { functions, db } from '../../lib/firebase';

// ---------- Types ----------
interface LoyaltyClient {
  id: string;
  displayName: string;
  email: string;
  status: string;
  pointsBalance: number;
  lifetimeEarned: number;
  totalRedeemed: number;
  loanCount: number;
  lastLoginAt: string | null;
  lastAccrualMonth: string | null;
  createdAt: string;
  officeId?: string | null;
  officeName?: string | null;
  brokerId?: string | null;
  brokerName?: string | null;
}

interface OfficeOption {
  id: string;
  name: string;
}

interface BrokerOption {
  id: string;
  name: string;
  officeId?: string;
}

interface GetLoyaltyClientsResponse {
  clients: LoyaltyClient[];
  hasMore: boolean;
}

interface InviteLoyaltyClientsResponse {
  invited: number;
  errors: Array<{ email: string; error: string }>;
}

interface CustomerNameResult {
  customerName: string;
  loanCount: number;
  totalTrailCents: number;
  latestPeriodMonth: string;
}

interface SearchCustomerNamesResponse {
  results: CustomerNameResult[];
}

interface MergeLoyaltyEntitiesResponse {
  linkedLoans: number;
  customerNames: string[];
  mergedNames: string[];
}

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-500/20 text-green-400 border border-green-500/30',
  invited: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
  inactive: 'bg-gray-500/20 text-gray-400 border border-gray-500/30',
};

const PAGE_SIZE = 10;

const LoyaltyClientsPage: React.FC = () => {
  const { companyId, claims } = useAuth();
  const userRole = claims?.role || '';
  const userOfficeIds = claims?.officeIds || [];
  const isBroker = userRole === 'broker';
  const isOfficeAdmin = userRole === 'office_admin';

  // Data state
  const [clients, setClients] = useState<LoyaltyClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Office & broker data
  const [allOffices, setAllOffices] = useState<OfficeOption[]>([]);
  const [allBrokers, setAllBrokers] = useState<BrokerOption[]>([]);

  // Filter state
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'invited' | 'inactive'>('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [officeFilter, setOfficeFilter] = useState('all');
  const [brokerFilter, setBrokerFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  // Sort state
  type SortKey = 'displayName' | 'pointsBalance' | 'lifetimeEarned' | 'loanCount' | 'tier' | 'createdAt';
  const [sortKey, setSortKey] = useState<SortKey>('pointsBalance');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteDisplayName, setInviteDisplayName] = useState('');
  const [inviteBackdatePeriod, setInviteBackdatePeriod] = useState<'0' | '3' | '6' | '12' | 'fy' | 'all'>('0');
  const [selectedCustomerNames, setSelectedCustomerNames] = useState<CustomerNameResult[]>([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState<CustomerNameResult[]>([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [inviteOfficeId, setInviteOfficeId] = useState('');
  const [inviteBrokerId, setInviteBrokerId] = useState('');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteMessage, setInviteMessage] = useState({ type: '', text: '' });

  // Manage modal state
  const [manageTarget, setManageTarget] = useState<LoyaltyClient | null>(null);
  const [manageAction, setManageAction] = useState<'adjustPoints' | 'rebackdate' | 'delete' | null>(null);
  const [adjustPoints, setAdjustPoints] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [backdatePeriod, setBackdatePeriod] = useState<'3' | '6' | '12' | 'fy' | 'all'>('3');
  const [manageSubmitting, setManageSubmitting] = useState(false);
  const [manageMessage, setManageMessage] = useState({ type: '', text: '' });
  const [openActionsId, setOpenActionsId] = useState<string | null>(null);

  // Merge modal state
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeTarget, setMergeTarget] = useState<LoyaltyClient | null>(null);
  const [mergeCustomerNames, setMergeCustomerNames] = useState('');
  const [mergeSubmitting, setMergeSubmitting] = useState(false);
  const [mergeMessage, setMergeMessage] = useState({ type: '', text: '' });

  // ---------- Fetch offices & brokers from Firestore ----------
  useEffect(() => {
    if (!companyId) return;

    let cancelled = false;

    const fetchOfficesAndBrokers = async () => {
      try {
        // Fetch offices
        const officesSnap = await getDocs(collection(db, `companies/${companyId}/offices`));
        const officesList: OfficeOption[] = officesSnap.docs
          .map((d) => ({ id: d.id, name: (d.data().name as string) || 'Unnamed Office' }))
          .sort((a, b) => a.name.localeCompare(b.name));

        // Fetch brokers
        const brokersSnap = await getDocs(collection(db, `companies/${companyId}/brokers`));
        const brokersList: BrokerOption[] = brokersSnap.docs
          .map((d) => {
            const data = d.data();
            return {
              id: d.id,
              name: (data.currentName as string) || (data.legalName as string) || (data.brokerDisplay as string) || 'Unknown Broker',
              officeId: (data.currentOfficeId as string) || (data.officeId as string) || undefined,
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name));

        if (!cancelled) {
          // RBAC: office_admin only sees their assigned offices
          if (isOfficeAdmin && userOfficeIds.length > 0) {
            const allowedSet = new Set(userOfficeIds);
            setAllOffices(officesList.filter((o) => allowedSet.has(o.id)));
            setAllBrokers(brokersList.filter((b) => !b.officeId || allowedSet.has(b.officeId)));
          } else {
            setAllOffices(officesList);
            setAllBrokers(brokersList);
          }
        }
      } catch (err) {
        console.error('[LoyaltyClientsPage] Failed to fetch offices/brokers:', err);
      }
    };

    fetchOfficesAndBrokers();
    return () => { cancelled = true; };
  }, [companyId, isOfficeAdmin, userOfficeIds]);

  // Reset broker filter when office filter changes
  useEffect(() => {
    setBrokerFilter('all');
    setCurrentPage(1);
  }, [officeFilter]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, brokerFilter]);

  // Brokers filtered by selected office (for filter dropdown)
  const filteredBrokerOptions = useMemo(() => {
    if (officeFilter === 'all') return allBrokers;
    return allBrokers.filter((b) => b.officeId === officeFilter);
  }, [allBrokers, officeFilter]);

  // Brokers filtered by selected invite office (for invite modal dropdown)
  const inviteBrokerOptions = useMemo(() => {
    if (!inviteOfficeId) return allBrokers;
    return allBrokers.filter((b) => b.officeId === inviteOfficeId);
  }, [allBrokers, inviteOfficeId]);

  // Reset invite broker when invite office changes
  useEffect(() => {
    setInviteBrokerId('');
  }, [inviteOfficeId]);

  // Debounced customer name search
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (!customerSearch.trim() || customerSearch.trim().length < 2 || !companyId) {
      setCustomerSearchResults([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setCustomerSearching(true);
      try {
        const searchFn = httpsCallable<
          { companyId: string; query: string; limit?: number },
          SearchCustomerNamesResponse
        >(functions, 'searchCustomerNames');

        const result = await searchFn({
          companyId,
          query: customerSearch.trim(),
          limit: 15,
        });

        // Filter out already-selected names
        const selectedSet = new Set(selectedCustomerNames.map((n) => n.customerName.toLowerCase()));
        setCustomerSearchResults(
          result.data.results.filter((r) => !selectedSet.has(r.customerName.toLowerCase()))
        );
      } catch (err) {
        console.error('[LoyaltyClientsPage] Customer search failed:', err);
        setCustomerSearchResults([]);
      } finally {
        setCustomerSearching(false);
      }
    }, 400);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [customerSearch, companyId, selectedCustomerNames]);

  // ---------- Fetch clients ----------
  const fetchClients = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    setError(null);

    try {
      const getLoyaltyClientsFn = httpsCallable<
        { companyId: string; status?: string; limit?: number },
        GetLoyaltyClientsResponse
      >(functions, 'getLoyaltyClients');

      const result = await getLoyaltyClientsFn({
        companyId,
        status: 'all',
        limit: 200,
      });

      setClients(result.data.clients);
    } catch (err: any) {
      console.error('[LoyaltyClientsPage] Failed to fetch clients:', err);
      setError(err?.message || 'Failed to load loyalty clients. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // ---------- Filter + paginate ----------
  const filteredClients = useMemo(() => {
    let result = clients;
    if (statusFilter !== 'all') {
      result = result.filter((c) => c.status === statusFilter);
    }
    if (tierFilter !== 'all') {
      result = result.filter((c) => computeTier(c.lifetimeEarned).current.id === tierFilter);
    }
    if (officeFilter !== 'all') {
      result = result.filter((c) => c.officeId === officeFilter);
    }
    if (brokerFilter !== 'all') {
      result = result.filter((c) => c.brokerId === brokerFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (c) => c.displayName.toLowerCase().includes(q) || c.email.toLowerCase().includes(q)
      );
    }
    // Sort
    result = [...result].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'displayName': cmp = a.displayName.localeCompare(b.displayName); break;
        case 'pointsBalance': cmp = a.pointsBalance - b.pointsBalance; break;
        case 'lifetimeEarned': cmp = a.lifetimeEarned - b.lifetimeEarned; break;
        case 'loanCount': cmp = a.loanCount - b.loanCount; break;
        case 'tier': cmp = computeTier(a.lifetimeEarned).current.minPoints - computeTier(b.lifetimeEarned).current.minPoints; break;
        case 'createdAt': cmp = (a.createdAt || '').localeCompare(b.createdAt || ''); break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [clients, statusFilter, tierFilter, officeFilter, brokerFilter, search, sortKey, sortDir]);

  // Sort handler
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  // Sort icon helper
  const SortIcon: React.FC<{ column: SortKey }> = ({ column }) => {
    if (sortKey !== column) return <ArrowUpDown className="w-3 h-3 text-gray-600" />;
    return sortDir === 'asc' ? <ArrowUp className="w-3 h-3 text-blue-400" /> : <ArrowDown className="w-3 h-3 text-blue-400" />;
  };

  const totalPages = Math.max(1, Math.ceil(filteredClients.length / PAGE_SIZE));
  const paginatedClients = filteredClients.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // ---------- Invite handler ----------
  const handleInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) {
      setInviteMessage({ type: 'error', text: 'Email is required.' });
      return;
    }
    if (!inviteDisplayName.trim()) {
      setInviteMessage({ type: 'error', text: 'Display name is required.' });
      return;
    }
    if (!companyId) {
      setInviteMessage({ type: 'error', text: 'Company context not available.' });
      return;
    }

    setInviteSubmitting(true);
    setInviteMessage({ type: '', text: '' });

    try {
      const customerNames = selectedCustomerNames.map((n) => n.customerName);

      const inviteLoyaltyClientsFn = httpsCallable<
        { companyId: string; clients: Array<{ email: string; displayName: string; loanIds: string[]; customerNames: string[]; backdatePeriod?: string }> },
        InviteLoyaltyClientsResponse
      >(functions, 'inviteLoyaltyClients');

      const result = await inviteLoyaltyClientsFn({
        companyId,
        clients: [
          {
            email: inviteEmail.trim(),
            displayName: inviteDisplayName.trim(),
            loanIds: [],
            customerNames,
            backdatePeriod: inviteBackdatePeriod,
          },
        ],
      });

      const { invited, errors } = result.data;

      if (errors.length > 0) {
        setInviteMessage({
          type: 'error',
          text: errors.map((e) => `${e.email}: ${e.error}`).join('; '),
        });
      } else if (invited > 0) {
        setInviteMessage({ type: 'success', text: `Invitation sent to ${inviteEmail}` });
        setInviteEmail('');
        setInviteDisplayName('');
        setSelectedCustomerNames([]);
        setCustomerSearch('');
        setCustomerSearchResults([]);
        setInviteBackdatePeriod('0');
        setInviteOfficeId('');
        setInviteBrokerId('');

        // Refresh client list after successful invite
        fetchClients();

        // Close modal after brief delay to show success
        setTimeout(() => {
          setInviteMessage({ type: '', text: '' });
          setShowInviteModal(false);
        }, 2000);
      }
    } catch (err: any) {
      console.error('[LoyaltyClientsPage] Failed to invite client:', err);
      setInviteMessage({
        type: 'error',
        text: err?.message || 'Failed to send invitation. Please try again.',
      });
    } finally {
      setInviteSubmitting(false);
    }
  };

  // ---------- Merge handler ----------
  const openMergeModal = (client: LoyaltyClient) => {
    setMergeTarget(client);
    setMergeCustomerNames('');
    setMergeMessage({ type: '', text: '' });
    setShowMergeModal(true);
  };

  const handleMergeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mergeTarget || !companyId) return;

    const names = mergeCustomerNames
      .split(',')
      .map((n) => n.trim())
      .filter(Boolean);

    if (names.length === 0) {
      setMergeMessage({ type: 'error', text: 'Enter at least one customer name.' });
      return;
    }

    setMergeSubmitting(true);
    setMergeMessage({ type: '', text: '' });

    try {
      const mergeFn = httpsCallable<
        { companyId: string; primaryClientId: string; customerNames: string[] },
        MergeLoyaltyEntitiesResponse
      >(functions, 'mergeLoyaltyEntities');

      const result = await mergeFn({
        companyId,
        primaryClientId: mergeTarget.id,
        customerNames: names,
      });

      const { linkedLoans } = result.data;

      if (linkedLoans === 0) {
        setMergeMessage({
          type: 'error',
          text: 'No new loans found matching those names. Check spelling matches your commission statements exactly.',
        });
      } else {
        setMergeMessage({
          type: 'success',
          text: `Successfully linked ${linkedLoans} loan${linkedLoans === 1 ? '' : 's'} to ${mergeTarget.displayName}.`,
        });

        // Refresh client list to show updated loan counts
        fetchClients();

        // Close modal after brief delay to show success
        setTimeout(() => {
          setMergeMessage({ type: '', text: '' });
          setShowMergeModal(false);
          setMergeTarget(null);
        }, 2500);
      }
    } catch (err: any) {
      console.error('[LoyaltyClientsPage] Failed to merge entities:', err);
      setMergeMessage({
        type: 'error',
        text: err?.message || 'Failed to merge entities. Please try again.',
      });
    } finally {
      setMergeSubmitting(false);
    }
  };

  // ---------- Manage handler ----------
  const handleManageSubmit = async () => {
    if (!manageTarget || !manageAction || !companyId) return;

    setManageSubmitting(true);
    setManageMessage({ type: '', text: '' });

    try {
      const manageFn = httpsCallable<
        { companyId: string; clientId: string; action: string; points?: number; reason?: string; backdatePeriod?: string },
        { success: boolean; action: string; points?: number; message?: string }
      >(functions, 'manageLoyaltyClient');

      let pts: number | undefined;
      if (manageAction === 'adjustPoints') {
        pts = parseInt(adjustPoints, 10);
        if (isNaN(pts) || pts === 0) {
          setManageMessage({ type: 'error', text: 'Enter a non-zero point amount.' });
          setManageSubmitting(false);
          return;
        }
      }

      const result = await manageFn({
        companyId,
        clientId: manageTarget.id,
        action: manageAction,
        points: pts,
        reason: manageAction === 'adjustPoints' ? (adjustReason || 'Manual adjustment') : undefined,
        backdatePeriod: manageAction === 'rebackdate' ? backdatePeriod : undefined,
      });

      if (manageAction === 'delete') {
        setManageMessage({ type: 'success', text: 'Client deleted.' });
        setTimeout(() => {
          setManageTarget(null);
          setManageAction(null);
          setManageMessage({ type: '', text: '' });
          fetchClients();
        }, 1000);
      } else if (manageAction === 'adjustPoints') {
        setManageMessage({ type: 'success', text: `${result.data.points! > 0 ? '+' : ''}${result.data.points} points applied.` });
        fetchClients();
      } else if (manageAction === 'rebackdate') {
        const pts = result.data.points || 0;
        setManageMessage({
          type: pts > 0 ? 'success' : 'error',
          text: pts > 0
            ? `${pts.toLocaleString()} points awarded from trail history.`
            : (result.data.message || 'No trail commissions found in that period.'),
        });
        fetchClients();
      }
    } catch (err: any) {
      console.error('[LoyaltyClientsPage] Manage action failed:', err);
      setManageMessage({ type: 'error', text: err?.message || 'Action failed. Please try again.' });
    } finally {
      setManageSubmitting(false);
    }
  };

  // ---------- Format date ----------
  const formatDate = (isoString: string | null): string => {
    if (!isoString) return '--';
    try {
      return new Date(isoString).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return '--';
    }
  };

  // Whether to show office/broker filters (hidden for broker role)
  const showFilters = !isBroker;
  const showOfficeFilter = showFilters && allOffices.length > 1;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Users}
        title="Loyalty Clients"
        subtitle="View and manage clients enrolled in your loyalty program"
        onRefresh={fetchClients}
        isRefreshing={loading}
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowInviteModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Invite Client
            </button>
          </div>
        }
      />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-gray-800/50 border border-gray-700/50 text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as any); setCurrentPage(1); }}
          className="px-4 py-2.5 rounded-lg bg-gray-800/50 border border-gray-700/50 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="invited">Invited</option>
          <option value="inactive">Inactive</option>
        </select>
        <select
          value={tierFilter}
          onChange={(e) => { setTierFilter(e.target.value); setCurrentPage(1); }}
          className="px-4 py-2.5 rounded-lg bg-gray-800/50 border border-gray-700/50 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="all">All Tiers</option>
          {DEFAULT_TIERS.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        {showOfficeFilter && (
          <select
            value={officeFilter}
            onChange={(e) => { setOfficeFilter(e.target.value); }}
            className="px-4 py-2.5 rounded-lg bg-gray-800/50 border border-gray-700/50 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="all">All Offices</option>
            {allOffices.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        )}
        {showFilters && (
          <select
            value={brokerFilter}
            onChange={(e) => { setBrokerFilter(e.target.value); setCurrentPage(1); }}
            className="px-4 py-2.5 rounded-lg bg-gray-800/50 border border-gray-700/50 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="all">All Brokers</option>
            {filteredBrokerOptions.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Loading State */}
      {loading && clients.length === 0 && (
        <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-12 flex flex-col items-center justify-center gap-3">
          <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
          <p className="text-sm text-gray-400">Loading loyalty clients...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-gray-800/50 border border-red-500/30 rounded-xl p-8 flex flex-col items-center justify-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-400" />
          <p className="text-sm text-red-300">{error}</p>
          <button
            onClick={fetchClients}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors mt-2"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      )}

      {/* Clients Table */}
      {!loading && !error && (
        <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl overflow-hidden">
          {filteredClients.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              {clients.length === 0 ? (
                <div className="space-y-2">
                  <Users className="w-8 h-8 mx-auto text-gray-600" />
                  <p>No loyalty clients yet</p>
                  <p className="text-xs text-gray-500">Click "Invite Client" to get started</p>
                </div>
              ) : (
                <p>No clients match your search criteria</p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-400">
                <thead className="text-xs text-gray-400 uppercase bg-gray-700/50">
                  <tr>
                    <th scope="col" className="px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('displayName')}>
                      <span className="inline-flex items-center gap-1">Name <SortIcon column="displayName" /></span>
                    </th>
                    <th scope="col" className="px-4 py-3">Email</th>
                    <th scope="col" className="px-4 py-3">Status</th>
                    <th scope="col" className="px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('tier')}>
                      <span className="inline-flex items-center gap-1">Tier <SortIcon column="tier" /></span>
                    </th>
                    <th scope="col" className="px-4 py-3 text-right cursor-pointer select-none" onClick={() => handleSort('pointsBalance')}>
                      <span className="inline-flex items-center gap-1 justify-end">Balance <SortIcon column="pointsBalance" /></span>
                    </th>
                    <th scope="col" className="px-4 py-3 text-right cursor-pointer select-none" onClick={() => handleSort('loanCount')}>
                      <span className="inline-flex items-center gap-1 justify-end">Loans <SortIcon column="loanCount" /></span>
                    </th>
                    <th scope="col" className="px-4 py-3">Office</th>
                    <th scope="col" className="px-4 py-3">Broker</th>
                    <th scope="col" className="px-4 py-3">Last Active</th>
                    <th scope="col" className="px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('createdAt')}>
                      <span className="inline-flex items-center gap-1">Joined <SortIcon column="createdAt" /></span>
                    </th>
                    <th scope="col" className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedClients.map((client) => (
                    <tr key={client.id} className="border-b border-gray-700/50 transition-colors hover:bg-gray-700/30">
                      <td className="px-4 py-4 font-medium text-white">{client.displayName || '--'}</td>
                      <td className="px-4 py-4">{client.email}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-block px-2.5 py-0.5 text-xs font-semibold uppercase rounded-full ${STATUS_STYLES[client.status] || STATUS_STYLES.inactive}`}>
                          {client.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {(() => {
                          const tier = computeTier(client.lifetimeEarned);
                          return (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full"
                              style={{ background: `${tier.current.color}20`, color: tier.current.color, border: `1px solid ${tier.current.color}40` }}
                            >
                              {tier.current.name}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 text-right font-medium text-white">
                        {client.pointsBalance.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-right">{client.loanCount}</td>
                      <td className="px-6 py-4">{client.officeName || '--'}</td>
                      <td className="px-6 py-4">{client.brokerName || '--'}</td>
                      <td className="px-6 py-4">
                        {formatDate(client.lastLoginAt)}
                      </td>
                      <td className="px-6 py-4">
                        {formatDate(client.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => openMergeModal(client)}
                            title="Merge entities"
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-blue-400 bg-blue-500/10 border border-blue-500/20 hover:bg-blue-500/20 hover:text-blue-300 transition-colors"
                          >
                            <Link2 className="w-3 h-3" />
                            Merge
                          </button>
                          <div className="relative">
                            <button
                              onClick={() => setOpenActionsId(openActionsId === client.id ? null : client.id)}
                              className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-gray-700/50 transition-colors"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                            {openActionsId === client.id && (
                              <div className="absolute right-0 top-full mt-1 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-20 py-1">
                                <button
                                  onClick={() => { setManageTarget(client); setManageAction('adjustPoints'); setAdjustPoints(''); setAdjustReason(''); setManageMessage({ type: '', text: '' }); setOpenActionsId(null); }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-gray-700/50 hover:text-white transition-colors"
                                >
                                  <PlusCircle className="w-4 h-4 text-green-400" />
                                  Adjust Points
                                </button>
                                <button
                                  onClick={() => { setManageTarget(client); setManageAction('rebackdate'); setBackdatePeriod('3'); setManageMessage({ type: '', text: '' }); setOpenActionsId(null); }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-gray-700/50 hover:text-white transition-colors"
                                >
                                  <CalendarClock className="w-4 h-4 text-blue-400" />
                                  Re-backdate Points
                                </button>
                                <div className="border-t border-gray-700 my-1" />
                                <button
                                  onClick={() => { setManageTarget(client); setManageAction('delete'); setManageMessage({ type: '', text: '' }); setOpenActionsId(null); }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Delete Client
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-gray-700/50">
              <p className="text-sm text-gray-400">
                Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, filteredClients.length)} of {filteredClients.length} clients
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-md border border-gray-600/50 text-gray-400 hover:text-white hover:bg-gray-700/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-400">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-md border border-gray-600/50 text-gray-400 hover:text-white hover:bg-gray-700/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Merge Entities Modal */}
      {showMergeModal && mergeTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => !mergeSubmitting && setShowMergeModal(false)} />
          <div className="relative w-full max-w-md bg-gray-800 border border-gray-700 rounded-xl shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white">Merge Entities</h3>
              <button
                onClick={() => !mergeSubmitting && setShowMergeModal(false)}
                disabled={mergeSubmitting}
                className="text-gray-400 hover:text-white transition-colors text-xl leading-none disabled:opacity-40"
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleMergeSubmit} className="p-6 space-y-4">
              <div className="bg-gray-700/50 border border-gray-600/50 rounded-lg p-4">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Primary Client</p>
                <p className="text-sm font-medium text-white">{mergeTarget.displayName}</p>
                <p className="text-sm text-gray-400">{mergeTarget.email}</p>
              </div>

              <div>
                <label htmlFor="merge-names" className="block text-sm font-medium text-gray-300 mb-2">
                  Additional Customer Names <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="merge-names"
                  value={mergeCustomerNames}
                  onChange={(e) => setMergeCustomerNames(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 min-h-[80px] resize-y"
                  placeholder="Smith Family Trust, Smith Holdings Pty Ltd"
                  required
                  disabled={mergeSubmitting}
                />
                <p className="text-xs text-gray-500 mt-1.5">
                  Enter names as they appear on commission statements, separated by commas (e.g., Smith Family Trust, Smith Holdings Pty Ltd)
                </p>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                <p className="text-xs text-blue-300">
                  Loans from these entities will be linked to {mergeTarget.displayName}&apos;s loyalty account so all points accrue together.
                </p>
              </div>

              {mergeMessage.text && (
                <div className={`p-3 rounded-md text-sm ${mergeMessage.type === 'success' ? 'bg-green-900/50 text-green-300' : 'bg-red-900/50 text-red-300'}`}>
                  {mergeMessage.text}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => !mergeSubmitting && setShowMergeModal(false)}
                  disabled={mergeSubmitting}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={mergeSubmitting}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed"
                >
                  {mergeSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {mergeSubmitting ? 'Merging...' : 'Merge Entities'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Client Modal */}
      {manageTarget && manageAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => !manageSubmitting && (setManageTarget(null), setManageAction(null))} />
          <div className="relative w-full max-w-md bg-gray-800 border border-gray-700 rounded-xl shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white">
                {manageAction === 'adjustPoints' && 'Adjust Points'}
                {manageAction === 'rebackdate' && 'Re-backdate Points'}
                {manageAction === 'delete' && 'Delete Client'}
              </h3>
              <button
                onClick={() => !manageSubmitting && (setManageTarget(null), setManageAction(null))}
                disabled={manageSubmitting}
                className="text-gray-400 hover:text-white transition-colors text-xl leading-none disabled:opacity-40"
              >
                &times;
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Client info */}
              <div className="bg-gray-700/50 border border-gray-600/50 rounded-lg p-3">
                <p className="text-sm font-medium text-white">{manageTarget.displayName}</p>
                <p className="text-xs text-gray-400">{manageTarget.email} &middot; {manageTarget.pointsBalance.toLocaleString()} pts balance</p>
              </div>

              {manageAction === 'adjustPoints' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Points (positive to add, negative to deduct)</label>
                    <input
                      type="number"
                      value={adjustPoints}
                      onChange={(e) => setAdjustPoints(e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="e.g. 500 or -200"
                      disabled={manageSubmitting}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Reason</label>
                    <input
                      type="text"
                      value={adjustReason}
                      onChange={(e) => setAdjustReason(e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="e.g. Correction for missed trail, Goodwill bonus"
                      disabled={manageSubmitting}
                    />
                  </div>
                </>
              )}

              {manageAction === 'rebackdate' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Points Tracking Start</label>
                  <select
                    value={backdatePeriod}
                    onChange={(e) => setBackdatePeriod(e.target.value as any)}
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    disabled={manageSubmitting}
                  >
                    <option value="3">Last 3 months</option>
                    <option value="6">Last 6 months</option>
                    <option value="12">Last 12 months</option>
                    <option value="fy">Current financial year</option>
                    <option value="all">All time</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Calculates points from linked loan trail commissions in the selected period. Points are ADDED to current balance.</p>
                </div>
              )}

              {manageAction === 'delete' && (
                <div className="bg-red-900/30 border border-red-500/30 rounded-lg p-3">
                  <p className="text-sm text-red-300">
                    This will permanently delete <strong>{manageTarget.displayName}</strong> and unlink all their loans. This cannot be undone.
                  </p>
                </div>
              )}

              {manageMessage.text && (
                <div className={`p-3 rounded-md text-sm ${manageMessage.type === 'success' ? 'bg-green-900/50 text-green-300' : 'bg-red-900/50 text-red-300'}`}>
                  {manageMessage.text}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => !manageSubmitting && (setManageTarget(null), setManageAction(null))}
                  disabled={manageSubmitting}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 transition-colors disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  onClick={handleManageSubmit}
                  disabled={manageSubmitting}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    manageAction === 'delete'
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {manageSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {manageAction === 'adjustPoints' && (manageSubmitting ? 'Applying...' : 'Apply Points')}
                  {manageAction === 'rebackdate' && (manageSubmitting ? 'Calculating...' : 'Calculate & Apply')}
                  {manageAction === 'delete' && (manageSubmitting ? 'Deleting...' : 'Delete Client')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite Client Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => !inviteSubmitting && setShowInviteModal(false)} />
          <div className="relative w-full max-w-md bg-gray-800 border border-gray-700 rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 sticky top-0 bg-gray-800 z-10">
              <h3 className="text-lg font-semibold text-white">Invite Client</h3>
              <button
                onClick={() => !inviteSubmitting && setShowInviteModal(false)}
                disabled={inviteSubmitting}
                className="text-gray-400 hover:text-white transition-colors text-xl leading-none disabled:opacity-40"
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleInviteSubmit} className="p-6 space-y-4">
              <div>
                <label htmlFor="invite-email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address <span className="text-red-400">*</span>
                </label>
                <input
                  id="invite-email"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="client@example.com"
                  required
                  disabled={inviteSubmitting}
                />
              </div>
              <div>
                <label htmlFor="invite-name" className="block text-sm font-medium text-gray-300 mb-2">
                  Display Name <span className="text-red-400">*</span>
                </label>
                <input
                  id="invite-name"
                  type="text"
                  value={inviteDisplayName}
                  onChange={(e) => setInviteDisplayName(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="John Smith"
                  required
                  disabled={inviteSubmitting}
                />
              </div>

              {/* Link Loans by Customer Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Link Loans by Customer Name
                </label>

                {/* Selected names */}
                {selectedCustomerNames.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {selectedCustomerNames.map((item) => (
                      <span
                        key={item.customerName}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30"
                      >
                        {item.customerName}
                        <span className="text-blue-400/70">({item.loanCount} loan{item.loanCount !== 1 ? 's' : ''})</span>
                        <button
                          type="button"
                          onClick={() => setSelectedCustomerNames((prev) => prev.filter((n) => n.customerName !== item.customerName))}
                          className="ml-0.5 text-blue-400 hover:text-white"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Search input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-700 border border-gray-600 text-white rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Search customer names from commission data..."
                    disabled={inviteSubmitting}
                  />
                  {customerSearching && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400 animate-spin" />
                  )}
                </div>

                {/* Search results dropdown */}
                {customerSearchResults.length > 0 && (
                  <div className="mt-1 bg-gray-700 border border-gray-600 rounded-md max-h-48 overflow-y-auto">
                    {customerSearchResults.map((result) => (
                      <button
                        key={result.customerName}
                        type="button"
                        onClick={() => {
                          setSelectedCustomerNames((prev) => [...prev, result]);
                          setCustomerSearch('');
                          setCustomerSearchResults([]);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-gray-600/50 transition-colors border-b border-gray-600/50 last:border-0"
                      >
                        <div>
                          <span className="text-white font-medium">{result.customerName}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-400">
                          <span>{result.loanCount} loan{result.loanCount !== 1 ? 's' : ''}</span>
                          <span>${(result.totalTrailCents / 100).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} trail</span>
                          <Plus className="w-3.5 h-3.5 text-blue-400" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {customerSearch.trim().length >= 2 && !customerSearching && customerSearchResults.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">No matching customer names found</p>
                )}
                {customerSearch.trim().length < 2 && selectedCustomerNames.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Search for customer names as they appear on commission statements to auto-link their loans
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="invite-backdate" className="block text-sm font-medium text-gray-300 mb-2">
                  Points Backdate
                </label>
                <select
                  id="invite-backdate"
                  value={inviteBackdatePeriod}
                  onChange={(e) => setInviteBackdatePeriod(e.target.value as '0' | '3' | '6' | '12' | 'fy' | 'all')}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  disabled={inviteSubmitting}
                >
                  <option value="0">No backdated points (start earning from now)</option>
                  <option value="3">Last 3 months</option>
                  <option value="6">Last 6 months</option>
                  <option value="12">Last 12 months</option>
                  <option value="fy">Current financial year (July to now)</option>
                  <option value="all">All time (all historical trail data)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Award points for historical trail commissions on linked loans</p>
              </div>

              {inviteMessage.text && (
                <div className={`p-3 rounded-md text-sm ${inviteMessage.type === 'success' ? 'bg-green-900/50 text-green-300' : 'bg-red-900/50 text-red-300'}`}>
                  {inviteMessage.text}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => !inviteSubmitting && setShowInviteModal(false)}
                  disabled={inviteSubmitting}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviteSubmitting}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed"
                >
                  {inviteSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {inviteSubmitting ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoyaltyClientsPage;
